import React, { useState, useEffect, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, setDoc, Timestamp, orderBy, limit, serverTimestamp, getCountFromServer } from "firebase/firestore";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";
import { FaChartLine, FaUsers, FaBox, FaArrowUp, FaArrowDown, FaSync, FaShieldAlt } from "react-icons/fa";

const CnfDashboardUltra = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Aggregate State
    const [stats, setStats] = useState({
        todayUob: 0,
        todaySalesPkts: 0,
        activeDistributors: 0,
        activeDs: 0,
        stockStatus: "OPTIMAL",
        totalBagsInStock: 0,
        lastUpdateTime: null
    });

    const [distributorPulse, setDistributorPulse] = useState([]);
    const [progressData, setProgressData] = useState([]);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                setUserProfile(profile);
                // Trigger Background Stock Sync for C&F
                getOrInitStock("cnf", profile.clientId, getISTDate(), profile.clientId).catch(console.error);
            }
        });
        return () => unsub();
    }, [navigate]);

    // OPTIMIZED DATA FETCHING: Read from a pre-calculated aggregate doc if possible
    const fetchBusinessIntelligence = async (forceRefresh = false) => {
        if (!userProfile) return;
        
        const today = getISTDate();
        const aggregateRef = doc(db, "aggregates", `${userProfile.clientId}_${today}`);

        // Rate limiting refresh to prevent cost spikes
        if (forceRefresh && isRefreshing) return;
        
        setIsRefreshing(true);
        
        try {
            const aggSnap = await getDoc(aggregateRef);
            
            // If it exists and not force refresh, use it (1 single read)
            if (aggSnap.exists()) {
                const data = aggSnap.data();
                const lastUpdate = data.updatedAt?.toDate() || new Date(0);
                const diffMinutes = (new Date() - lastUpdate) / (1000 * 60);

                // If not force refresh OR it's been less than 15 mins since last deep sync
                if (!forceRefresh || diffMinutes < 15) {
                    setStats(data.stats);
                    setDistributorPulse(data.distributorPulse || []);
                    setProgressData(data.progressData || []);
                    setLoading(false);
                    setIsRefreshing(false);
                    if (forceRefresh && diffMinutes < 15) {
                        console.log(`Deep sync throttled. Last update was ${Math.round(diffMinutes)} mins ago.`);
                    }
                    return;
                }
            }

            // Otherwise, perform the "Expensive" one-time aggregation
            console.log("PERFORMING REAL-TIME AGGREGATION...");
            
            // 1. Get Distributors from partyMaster scoped to clientId
            const partySnap = await getDocs(query(
                collection(db, "partyMaster"),
                where("clientId", "==", userProfile.clientId)
            ));
            const activeParties = partySnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // 2. Get Today's Distributor Invoices (C&F to Distributor)
            const cnfSalesSnap = await getDocs(query(
                collection(db, "distributor_invoices"),
                where("clientId", "==", userProfile.clientId),
                where("approvedDateOnly", "==", today),
                where("source", "==", "CNF_TO_DISTRIBUTOR")
            ));

            let currentSalesRate = 0;
            const distSalesMap = {};
            cnfSalesSnap.forEach(d => {
                const data = d.data();
                const it = data.items || {};
                let invoiceTotal = 0;
                Object.values(it).forEach(qty => invoiceTotal += Number(qty));
                currentSalesRate += invoiceTotal;
                distSalesMap[data.distributorId] = (distSalesMap[data.distributorId] || 0) + invoiceTotal;
            });

            // 3. Get Today's Outlet Orders (Market Penetration / UOB)
            const ordersSnap = await getDocs(query(
                collection(db, "Outlet_Orders"),
                where("clientId", "==", userProfile.clientId),
                where("orderDateOnly", "==", today)
            ));

            const uniqueOutlets = new Set();
            const activeDsIds = new Set();
            const distUobMap = {};
            
            ordersSnap.forEach(d => {
                const data = d.data();
                uniqueOutlets.add(data.outletId || data.shopId);
                activeDsIds.add(data.salesmanUid || data.dsId);
                
                // Track UOB per distributor node
                if (data.distributorId) {
                    const uobSet = distUobMap[data.distributorId] || new Set();
                    uobSet.add(data.outletId || data.shopId);
                    distUobMap[data.distributorId] = uobSet;
                }
            });

            // 4. Stock Status (From C&F Daily Stock)
            const stockId = `${userProfile.clientId}_${today}`;
            const stockSnap = await getDoc(doc(db, "cnf_stock_daily", stockId));
            let totalClosingPkts = 0;
            if (stockSnap.exists()) {
                const inv = stockSnap.data().inventory || {};
                Object.values(inv).forEach(item => {
                    totalClosingPkts += (item.opening || 0) + (item.purchase || 0) - (item.sale || 0);
                });
            }

            // 5. Total Register Outlets (Market Potential) - CRITICAL: Using getCountFromServer for Zero-Read cost efficiency
            const allOutletsQuery = query(
                collection(db, "outletMaster"), 
                where("clientId", "==", userProfile.clientId)
            );
            const countSnap = await getCountFromServer(allOutletsQuery);
            const totalOutletsCount = countSnap.data().count;

            // 6. Recent Acquisitions (PDF Flow) - Get real last 5
            const lastPurchasesSnap = await getDocs(query(
                collection(db, "cnf_purchases"),
                where("clientId", "==", userProfile.clientId),
                orderBy("createdAt", "desc"),
                limit(5)
            ));
            const recentInflow = lastPurchasesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Prepare Distributor Pulse Grid
            const pulse = activeParties.map(p => ({
                name: p.name,
                pkts: distSalesMap[p.partyId] || 0,
                uob: distUobMap[p.partyId]?.size || 0,
                targetComp: 100 // Placeholder for target
            }));

            const finalStats = {
                todayUob: uniqueOutlets.size,
                todaySalesPkts: currentSalesRate,
                activeDistributors: activeParties.length,
                activeDs: activeDsIds.size,
                stockStatus: totalClosingPkts < 5000 ? "CRITICAL" : totalClosingPkts < 20000 ? "LOW" : "OPTIMAL",
                totalBagsInStock: Math.max(0, Math.floor(totalClosingPkts / 100)), // 100pkts per bag approx
                lastUpdateTime: Timestamp.now(),
                totalOutlets: totalOutletsCount
            };

            // Save for future "Cheap" reads
            const payload = {
                stats: finalStats,
                distributorPulse: pulse,
                recentInflow: recentInflow,
                progressData: [
                    { day: "Today", sales: currentSalesRate },
                ],
                updatedAt: serverTimestamp()
            };
            
            await setDoc(aggregateRef, payload, { merge: true });

            setStats(finalStats);
            setDistributorPulse(pulse);
            // We use history from prev logic or just this one
            setProgressData(payload.progressData);
            setLoading(false);
            setIsRefreshing(false);
        } catch (err) {
            console.error("Aggregation Error:", err);
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (userProfile) fetchBusinessIntelligence();
    }, [userProfile]);

    const MetricCard = ({ title, value, sub, icon, trend, color }) => (
        <div className="premium-card" style={{ padding: 24, position: "relative", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                    <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 }}>{title}</span>
                    <div style={{ fontSize: 32, fontWeight: 900, marginTop: 4, color: "var(--text-main)" }}>{value}</div>
                    <div style={{ fontSize: 12, marginTop: 4, fontWeight: 600, color: color || "var(--text-muted)" }}>{sub}</div>
                </div>
                <div style={{ padding: 12, borderRadius: 12, background: "rgba(99, 102, 241, 0.05)", color: "var(--primary)", fontSize: 20 }}>{icon}</div>
            </div>
            {trend && (
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: color || "var(--primary)", opacity: 0.2 }}></div>
            )}
        </div>
    );

    if (loading) return <Layout><div style={{ padding: 100, textAlign: "center" }}>Encrypting Data Stream...</div></Layout>;

    return (
        <Layout>
            <div className="layout-content">
                {/* DYNAMIC HEADER */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40 }}>
                    <div>
                        <h1 style={{ fontSize: 36, fontWeight: 900, marginBottom: 4, letterSpacing: "-0.03em" }}>COMMAND CENTER</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Unified Intelligence for {userProfile?.clientName || "Primary Hub"}</p>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                        {/* Master Password for Distributors */}
                        <div style={{ background: "rgba(99, 102, 241, 0.1)", padding: "8px 16px", borderRadius: 12, border: "1px solid var(--primary)", marginRight: 12 }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: "var(--primary)", textTransform: "uppercase" }}>Master Sync Key</div>
                            <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: 2 }}>
                                {require("../utils/dateUtils").getMasterKey()}
                            </div>
                        </div>

                        <div style={{ textAlign: "right", marginRight: 12 }}>


                            <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)" }}>LAST SYNC</div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{stats.lastUpdateTime?.toDate().toLocaleTimeString() || "Live"}</div>
                        </div>
                        <button onClick={() => fetchBusinessIntelligence(true)} className={`btn-secondary ${isRefreshing ? "spin" : ""}`} style={{ borderRadius: "50%", padding: 16, width: 54, height: 54 }}>
                            <FaSync />
                        </button>
                    </div>
                </div>

                {/* TOP METRICS - MARKET POWER */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24, marginBottom: 40 }}>
                    <MetricCard 
                        title="Market Penetration (UOB)" 
                        value={stats.todayUob} 
                        sub="TOTAL OUTLETS BILLED TODAY" 
                        icon={<FaUsers />} 
                        color="var(--success)"
                    />
                    <MetricCard 
                        title="Sales Velocity" 
                        value={stats.todaySalesPkts.toLocaleString()} 
                        sub="PACKETS MOVED (LAST 24H)" 
                        icon={<FaChartLine />} 
                        color="var(--primary)"
                    />
                    <MetricCard 
                        title="Army Deployment" 
                        value={`${stats.activeDs} / 80`} 
                        sub="ACTIVE SALESMEN ON BEAT" 
                        icon={<FaShieldAlt />} 
                        color="var(--warning)"
                    />
                    <MetricCard 
                        title="Stock Runway" 
                        value={`${stats.totalBagsInStock} Bags`} 
                        sub={stats.stockStatus === "CRITICAL" ? "IMMEDIATE REFILL REQUIRED" : `${Math.floor(stats.totalBagsInStock / 40)} DAYS REMAINING`} 
                        icon={<FaBox />} 
                        color={stats.stockStatus === "CRITICAL" ? "var(--error)" : "var(--success)"}
                    />
                </div>

                {/* THE GROWTH MAP & LOGISTICS */}
                <div style={{ display: "grid", gridTemplateColumns: "1.8fr 1fr", gap: 32 }}>
                    
                    {/* MARKET SHARE GROWTH - BATTLE MAP ORIENTATION */}
                    <div className="premium-card" style={{ padding: 32 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 32 }}>
                            <h3 style={{ margin: 0, fontWeight: 900 }}>DISTRIBUTOR ACTIVATION ({distributorPulse.length} NODES)</h3>
                            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--success)" }}><FaArrowUp /> LIVE FEED</span>
                        </div>
                        
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                            {distributorPulse.map((dist, i) => (
                                <div key={i} style={{ padding: 16, borderRadius: 16, border: "1px solid var(--border)", background: "var(--surface)" }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dist.name.toUpperCase()}</div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 8 }}>
                                        <div style={{ fontSize: 18, fontWeight: 900 }}>{dist.uob} <span style={{ fontSize: 10, color: "var(--text-muted)" }}>UOB</span></div>
                                        <div style={{ width: 40, height: 4, background: dist.uob > 0 ? "var(--success)" : "var(--border)", borderRadius: 2 }}></div>
                                    </div>
                                    <div style={{ fontSize: 9, marginTop: 4, fontWeight: 700 }}>{dist.pkts.toLocaleString()} PKTS MOVED</div>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: 40, padding: 24, borderRadius: 16, background: "rgba(99, 102, 241, 0.03)", border: "1px dashed var(--primary)" }}>
                            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                                <div style={{ fontSize: 24 }}>🚀</div>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 15 }}>MARKET EXPANSION OPPORTUNITY</div>
                                    <p style={{ margin: "4px 0 0 0", fontSize: 13, color: "var(--text-muted)" }}>
                                        {((stats.totalOutlets || 0) - stats.todayUob).toLocaleString()} shops identified in master list but not yet activated today.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RECENT ACQUISITIONS (PDF FLOW) */}
                    <div className="premium-card" style={{ padding: 32 }}>
                        <h3 style={{ margin: "0 0 24px 0", fontWeight: 900 }}>INFLOW RECONCILIATION</h3>
                        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                            {stats.recentInflow && stats.recentInflow.length > 0 ? (
                                stats.recentInflow.map((pur, i) => (
                                    <div key={i} style={{ display: "flex", justifyContent: "space-between", paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
                                        <div>
                                            <div style={{ fontWeight: 800, fontSize: 14 }}>{pur.invoiceNo || "N/A"}</div>
                                            <div style={{ fontSize: 11, color: "var(--text-muted)", maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pur.supplierName}</div>
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontWeight: 800, color: "var(--success)" }}>+{pur.qtyPkt} PKTS</div>
                                            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{pur.createdAt?.toDate().toLocaleDateString()}</div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p style={{ textAlign: "center", color: "var(--text-muted)", padding: 40 }}>No recent purchases detected.</p>
                            )}
                        </div>
                        <button className="btn-secondary" style={{ width: "100%", marginTop: 24, padding: 14 }} onClick={() => navigate("/cnf-purchase")}>LOAD NEW INVOICE</button>
                    </div>

                </div>
            </div>

            <style>{`
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { 100% { transform: rotate(360deg); } }
                .premium-card:hover { border-color: var(--primary); transform: translateY(-2px); transition: all 0.3s ease; }
            `}</style>
        </Layout>
    );
};

export default CnfDashboardUltra;
